import { MOVE, type Box, type Cylinder } from "@eldrun/sim";

export interface CameraPose {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
}

/**
 * Plano near da câmera do jogo (m). O pipeline de modelos (tools/assets, `trunkClearance`) deixa a
 * casca das árvores pelo menos isso longe do olho de quem para no colisor; a câmera em terceira pessoa
 * guarda a mesma folga (a esfera dela tem o raio do corpo, `CAMERA.radius`).
 */
export const CAMERA_NEAR = 0.1;

/** Enquadramento: braço, ombro (à direita), altura do pivô sobre os pés (m) e acréscimo de FOV (°). */
export interface Framing {
  arm: number;
  shoulder: number;
  pivot: number;
  fov: number;
}

/**
 * Câmera em terceira pessoa (F55, `docs/game/camera.md`): números afinados para a câmera de ombro.
 * O braço é o do doc 02 (3,5 m); o ombro (0,7 m) e o pivô (1,7 m, acima da cabeça) põem o boneco no
 * terço de baixo à esquerda, com a cruz livre à direita dele, como nas câmeras de ombro do mercado.
 */
export const CAMERA = {
  /** Raio da esfera da câmera: o do corpo, para a câmera ter a folga do olho (casca, laje da Gruta). */
  radius: MOVE.radius,
  /** Molas que seguem o corpo (1/s) e o atraso máximo da cópia suavizada (m). */
  followH: 26,
  followV: 10,
  maxLagH: 1,
  maxLagV: 0.9,
  /** Salto maior que isto (m) encaixa na hora (nascer, voltar, travessia). */
  snap: 8,
  /** Volta do braço depois de uma colisão (1/s); encolher é na hora. */
  armReturn: 3.5,
  /** Braço mínimo (m): encostada, a câmera fica junto do pivô. */
  minArm: 0.05,
  /** Câmera a menos disto da cabeça (m): o boneco some em pontilhado (todo a `fadeGone`). */
  fadeStart: 1.6,
  fadeGone: 0.9,
  /** Fora d'água, a câmera fica pelo menos isto acima do mar (m). */
  seaClearance: 0.2,
  /** Teto (caixa) até isto acima do pivô conta como "sob teto" (m). */
  coverHeight: 3,
  /** Ombro: o boneco fica a ~40% da tela (o padrão das câmeras de ombro). Mais aberto que 0,85 m, um
   * alvo a 2 m sob a cruz passaria dos 25° de desvio que o servidor aceita entre a mira e a câmera. */
  normal: { arm: 3.6, shoulder: 0.85, pivot: 1.8, fov: 0 },
  sprint: { arm: 3.95, shoulder: 0.85, pivot: 1.8, fov: 7 },
  crouch: { arm: 3.2, shoulder: 0.8, pivot: 1.2, fov: 0 },
  swim: { arm: 4, shoulder: 0.65, pivot: 1.95, fov: 0 },
  carried: { arm: 5.2, shoulder: 0.5, pivot: 2.2, fov: 5 },
  aim: { arm: 2.6, shoulder: 1, pivot: 2.05, fov: -10 },
  /** Sob teto: o braço multiplica por isto e o ombro vai a 0,5 m. */
  coveredArm: 0.7,
  coveredShoulder: 0.5,
  /** Taxas dos pesos (1/s). */
  rates: { sprint: 4, crouch: 8, swim: 3, carried: 2, covered: 3, aimIn: 14, aimOut: 9 },
} as const;

/** O que a câmera precisa saber do corpo e do jogador neste quadro. */
export interface CameraInput {
  /** Segundos desde o último quadro (já presos em 0,1 s). */
  dt: number;
  /** Pés do corpo previsto. */
  x: number;
  y: number;
  z: number;
  /** Olhar do mouse, direto. */
  yaw: number;
  pitch: number;
  /** Velocidade horizontal (m/s). */
  speed: number;
  grounded: boolean;
  crouching: boolean;
  swimming: boolean;
  /** Olhos embaixo d'água (mergulho): a câmera pode entrar na água. */
  submerged: boolean;
  /** Carregado pela Jangada. */
  carried: boolean;
  /** Puxando o arco: aproxima do ombro. */
  aiming: boolean;
  seaLevel: number;
}

/**
 * O mundo que a câmera enxerga para colidir: o da predição (`HeightfieldWorld`), pelas buscas que
 * escrevem por índice e devolvem a contagem (sem alocar a cada quadro).
 */
export interface CameraWorld {
  height(x: number, z: number): number;
  cylindersInto(x: number, z: number, radius: number, out: Cylinder[]): number;
  boxesInto(x: number, z: number, radius: number, out: Box[]): number;
}

/**
 * Mola criticamente amortecida, na solução fechada, com o alvo andando em linha reta durante o passo
 * (a velocidade dele vem do alvo anterior): estável e igual em qualquer dt, também seguindo um corpo
 * que corre (o atraso fica em 2v/ω a 240 fps e a 10 fps). Lê e grava a posição, a velocidade e o alvo
 * anterior em `s[i]`, `s[i + 1]` e `s[i + 2]`.
 */
export function springTo(s: Float64Array, i: number, target: number, omega: number, dt: number): void {
  if (dt <= 0) return;
  const u = (target - (s[i + 2] as number)) / dt;
  // Erro em volta do regime (atraso de 2u/ω atrás do alvo que anda a u): decai como mola parada.
  const f0 = (s[i] as number) - (s[i + 2] as number) + (2 * u) / omega;
  const g0 = (s[i + 1] as number) - u;
  const k = g0 + omega * f0;
  const e = Math.exp(-omega * dt);
  s[i] = target + (f0 + k * dt) * e - (2 * u) / omega;
  s[i + 1] = (g0 - omega * k * dt) * e + u;
  s[i + 2] = target;
}

/** Aproximação exponencial de `current` a `target` pela taxa (1/s), igual em qualquer dt. */
export const approach = (current: number, target: number, rate: number, dt: number): number => current + (target - current) * (1 - Math.exp(-rate * dt));

const cylinders: Cylinder[] = [];

/**
 * Chão sob a esfera: o terreno no centro e a `r` dele em x e z (o maior). Com `r = 0`, só o centro (o
 * raio da cruz). Assim a esfera não encosta de lado num barranco que a amostra do centro não vê.
 */
function groundUnder(world: CameraWorld, x: number, z: number, r: number): number {
  let h = world.height(x, z);
  if (r <= 0) return h;
  // Comparações em sequência (o `Math.max` de cinco chamadas punha cada altura numa caixa).
  let v = world.height(x + r, z);
  if (v > h) h = v;
  v = world.height(x - r, z);
  if (v > h) h = v;
  v = world.height(x, z + r);
  if (v > h) h = v;
  v = world.height(x, z - r);
  if (v > h) h = v;
  return h;
}
const boxes: Box[] = [];

/**
 * Entrada do segmento no cilindro inflado (fração; `Infinity` sem contato). Começando dentro ou na
 * borda (o pivô que o ombro deixou encostado), só conta se o segmento vai para dentro: de lado ou para
 * fora (tangente ao tronco ao lado, braço saindo da parede) segue livre, senão a câmera colava na
 * cabeça por um arredondamento (achado na revisão da F55).
 */
function cylinderEntry(ax: number, ay: number, az: number, dx: number, dy: number, dz: number, c: Cylinder, r: number): number {
  const radius = c.radius + r;
  const bottom = c.bottom - r;
  const top = c.top + r;
  const fx = ax - c.x;
  const fz = az - c.z;
  const qa = dx * dx + dz * dz;
  const qc = fx * fx + fz * fz - radius * radius;
  let t0 = 0;
  let t1 = 1;
  if (qa < 1e-12) {
    if (qc > 0) return Infinity;
  } else {
    const qb = 2 * (fx * dx + fz * dz);
    if (qc <= 0 && qb >= 0) return Infinity;
    const disc = qb * qb - 4 * qa * qc;
    if (disc < 0) return Infinity;
    const root = Math.sqrt(disc);
    t0 = Math.max(0, (-qb - root) / (2 * qa));
    t1 = Math.min(1, (-qb + root) / (2 * qa));
    if (t0 > t1) return Infinity;
  }
  if (Math.abs(dy) < 1e-12) return ay >= bottom && ay <= top ? t0 : Infinity;
  const ya = (bottom - ay) / dy;
  const yb = (top - ay) / dy;
  const start = Math.max(t0, ya < yb ? ya : yb);
  return start <= Math.min(t1, ya < yb ? yb : ya) ? start : Infinity;
}

/** Uma faixa do método das faixas: estreita [t0, t1] em `slab` (buffer: sem número em caixa) ou `false` se esvaziou. */
const slab = new Float64Array(2);
function clip(o: number, d: number, lo: number, hi: number): boolean {
  if (Math.abs(d) < 1e-12) return o >= lo && o <= hi;
  const ua = (lo - o) / d;
  const ub = (hi - o) / d;
  const u0 = ua < ub ? ua : ub;
  const u1 = ua < ub ? ub : ua;
  if (u0 > (slab[0] as number)) slab[0] = u0;
  if (u1 < (slab[1] as number)) slab[1] = u1;
  return (slab[0] as number) <= (slab[1] as number);
}

/** Penetração e saída pela face mais perto num eixo: `exit` > 0 se o movimento `d` sai por ela. */
const nearest = new Float64Array(2);
function nearestFace(o: number, d: number, lo: number, hi: number): void {
  const toLo = o - lo;
  const toHi = hi - o;
  nearest[0] = toLo < toHi ? toLo : toHi;
  nearest[1] = toLo < toHi ? -d : d;
}

/**
 * Entrada do segmento na caixa inflada (a rampa conta como a caixa inteira: conservador). Começando
 * dentro ou na borda, só conta se o segmento não sai pela face mais perto (como no cilindro).
 */
function boxEntry(ax: number, ay: number, az: number, dx: number, dy: number, dz: number, b: Box, r: number): number {
  slab[0] = 0;
  slab[1] = 1;
  const x0 = b.minX - r;
  const x1 = b.maxX + r;
  const y0 = b.minY - r;
  const y1 = b.maxY + r;
  const z0 = b.minZ - r;
  const z1 = b.maxZ + r;
  if (!clip(ax, dx, x0, x1) || !clip(ay, dy, y0, y1) || !clip(az, dz, z0, z1)) return Infinity;
  if ((slab[0] as number) > 0) return slab[0] as number;
  // Começa dentro: a face mais perto decide (saindo ou de lado por ela, livre).
  nearestFace(ax, dx, x0, x1);
  let depth = nearest[0] as number;
  let exit = nearest[1] as number;
  nearestFace(ay, dy, y0, y1);
  if ((nearest[0] as number) < depth) {
    depth = nearest[0] as number;
    exit = nearest[1] as number;
  }
  nearestFace(az, dz, z0, z1);
  if ((nearest[0] as number) < depth) exit = nearest[1] as number;
  return exit >= 0 ? Infinity : 0;
}

/**
 * Varredura de uma esfera de raio `r` de A a B contra o terreno, os cilindros e as caixas do mundo:
 * fração do caminho até o primeiro contato (1 = livre, 0 = já começa encostada). Sem alocação. O
 * terreno é amostrado a cada `step` m (centro a `r` acima do chão sob a esfera, `groundUnder`), com
 * bisseção no trecho do contato; cilindros e caixas, inflados pelo raio, por teste exato.
 */
export function sweepSphere(world: CameraWorld, ax: number, ay: number, az: number, bx: number, by: number, bz: number, r: number, step = 0.25): number {
  const dx = bx - ax;
  const dy = by - ay;
  const dz = bz - az;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 1e-6) return 1;
  let best = 1;
  if (ay - r < groundUnder(world, ax, az, r)) return 0;
  const steps = Math.max(2, Math.ceil(len / step));
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    if (ay + dy * t - r >= groundUnder(world, ax + dx * t, az + dz * t, r)) continue;
    let lo = (i - 1) / steps;
    let hi = t;
    for (let k = 0; k < 6; k++) {
      const mid = (lo + hi) / 2;
      if (ay + dy * mid - r < groundUnder(world, ax + dx * mid, az + dz * mid, r)) hi = mid;
      else lo = mid;
    }
    best = lo;
    break;
  }
  const mx = ax + dx / 2;
  const mz = az + dz / 2;
  const reach = Math.sqrt(dx * dx + dz * dz) / 2 + r;
  const nc = world.cylindersInto(mx, mz, reach, cylinders);
  for (let i = 0; i < nc; i++) {
    const t = cylinderEntry(ax, ay, az, dx, dy, dz, cylinders[i] as Cylinder, r);
    if (t < best) best = t;
  }
  const nb = world.boxesInto(mx, mz, reach, boxes);
  for (let i = 0; i < nb; i++) {
    const t = boxEntry(ax, ay, az, dx, dy, dz, boxes[i] as Box, r);
    if (t < best) best = t;
  }
  return best;
}

/** Alguma caixa cobre (x, z) com a base entre `y` e `y + height`? (a laje da Gruta, um telhado). */
export function coveredAt(world: CameraWorld, x: number, y: number, z: number, height: number): boolean {
  const n = world.boxesInto(x, z, 0, boxes);
  let covered = false;
  for (let i = 0; i < n; i++) {
    const b = boxes[i] as Box;
    if (x >= b.minX && x <= b.maxX && z >= b.minZ && z <= b.maxZ && b.minY > y && b.minY <= y + height) covered = true;
  }
  return covered;
}

/** Um campo do enquadramento: o normal mais as diferenças dos estados (correr, agachar, nadar, Jangada) pelos pesos. */
function mix(w: { sprint: number; crouch: number; swim: number; carried: number }, key: keyof Framing): number {
  const n = CAMERA.normal[key];
  return n + w.sprint * (CAMERA.sprint[key] - n) + w.crouch * (CAMERA.crouch[key] - n) + w.swim * (CAMERA.swim[key] - n) + w.carried * (CAMERA.carried[key] - n);
}

const smoothstep = (a: number, b: number, v: number): number => {
  const t = Math.max(0, Math.min(1, (v - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/**
 * Câmera de ombro (F55): segue uma cópia suavizada do corpo (molas por eixo, vertical mais mole),
 * gira direto com o mouse, mistura o enquadramento por estado e colide por esfera (encolhe na hora,
 * volta devagar). Um objeto por cena; nada é alocado por quadro.
 */
export class ThirdPersonCamera {
  /** Pose deste quadro (sem tremor) e o FOV somado à opção. */
  readonly pose = { x: 0, y: 0, z: 0, yaw: 0, pitch: 0, fov: 0 };
  /** Pivô (sobre o ombro) deste quadro. */
  readonly pivot = { x: 0, y: 0, z: 0 };
  /** Enquadramento misturado deste quadro. */
  readonly framing: Framing = { ...CAMERA.normal };
  /** Braço depois da colisão (m) e quanto o próprio boneco sumiu (0 a 1). */
  arm: number = CAMERA.normal.arm;
  fade = 0;
  /** Pesos dos estados (0 a 1). */
  readonly weights = { sprint: 0, crouch: 0, swim: 0, carried: 0, covered: 0, aim: 0 };
  /** Cópia suavizada do corpo: x, vx, alvo x anterior, y, vy, alvo y, z, vz, alvo z. */
  private readonly follow = new Float64Array(9);
  private started = false;

  /** Recomeça encaixada no corpo (nascer, voltar da morte). */
  reset(): void {
    this.started = false;
  }

  /** Posição suavizada do corpo (pés). */
  get body(): { x: number; y: number; z: number } {
    return { x: this.follow[0] as number, y: this.follow[3] as number, z: this.follow[6] as number };
  }

  update(input: CameraInput, world: CameraWorld): CameraPose & { fov: number } {
    const dt = Math.max(0, input.dt);
    this.followBody(input, dt);
    this.blendFraming(input, world, dt);
    const f = this.framing;
    const s = this.follow;
    const sin = Math.sin(input.yaw);
    const cos = Math.cos(input.yaw);
    const cp = Math.cos(input.pitch);
    const sp = Math.sin(input.pitch);
    const r = CAMERA.radius;
    // Cabeça (sem ombro) e o ombro à direita = (cos, 0, −sin), recolhido se uma parede estiver ao lado.
    const hx = s[0] as number;
    const hy = (s[3] as number) + f.pivot;
    const hz = s[6] as number;
    // O ombro para um fio (2 cm) antes do contato: o braço parte de fora da borda.
    const reach = sweepSphere(world, hx, hy, hz, hx + cos * f.shoulder, hy, hz - sin * f.shoulder, r);
    const side = reach < 1 ? Math.max(0, f.shoulder * reach - 0.02) : f.shoulder;
    const px = hx + cos * side;
    const pz = hz - sin * side;
    this.pivot.x = px;
    this.pivot.y = hy;
    this.pivot.z = pz;
    // Para trás = −frente = (sin·cp, −sp, cos·cp).
    const bx = sin * cp;
    const by = -sp;
    const bz = cos * cp;
    const free = Math.max(CAMERA.minArm, f.arm * sweepSphere(world, px, hy, pz, px + bx * f.arm, hy + by * f.arm, pz + bz * f.arm, r));
    // Encolhe na hora; volta devagar, nunca além do que está livre.
    this.arm = Math.min(free, this.started ? approach(Math.min(this.arm, f.arm), f.arm, CAMERA.armReturn, dt) : f.arm);
    this.started = true;
    const pose = this.pose;
    pose.x = px + bx * this.arm;
    pose.y = hy + by * this.arm;
    pose.z = pz + bz * this.arm;
    // Fora d'água, a câmera não mergulha (só com os olhos embaixo d'água).
    if (!input.submerged && hy > input.seaLevel + CAMERA.seaClearance) pose.y = Math.max(pose.y, input.seaLevel + CAMERA.seaClearance);
    pose.yaw = input.yaw;
    pose.pitch = input.pitch;
    pose.fov = f.fov;
    // O boneco some em pontilhado com a câmera colada na cabeça (centro a ~0,8 do pivô).
    const head = (s[3] as number) + f.pivot * 0.8;
    const d = Math.sqrt((pose.x - hx) * (pose.x - hx) + (pose.y - head) * (pose.y - head) + (pose.z - hz) * (pose.z - hz));
    this.fade = 1 - smoothstep(CAMERA.fadeGone, CAMERA.fadeStart, d);
    return pose;
  }

  /**
   * Olhar que põe o raio da câmera sobre o ponto (x, y, z), com o pivô e o braço deste quadro (os e2e
   * miram assim; sem pointer lock no headless). Ponto fixo de poucas iterações: o pivô depende do yaw.
   */
  lookThrough(x: number, y: number, z: number, out: { yaw: number; pitch: number }): { yaw: number; pitch: number } {
    const s = this.follow;
    const f = this.framing;
    const hx = this.started ? (s[0] as number) : x;
    const hz = this.started ? (s[6] as number) : z;
    const hy = (s[3] as number) + f.pivot;
    let yaw = Math.atan2(-(x - hx), -(z - hz));
    let pitch = Math.atan2(y - hy, Math.hypot(x - hx, z - hz));
    for (let i = 0; i < 8; i++) {
      const sin = Math.sin(yaw);
      const cos = Math.cos(yaw);
      const px = hx + cos * f.shoulder;
      const pz = hz - sin * f.shoulder;
      const cx = px + sin * Math.cos(pitch) * this.arm;
      const cy = hy - Math.sin(pitch) * this.arm;
      const cz = pz + cos * Math.cos(pitch) * this.arm;
      yaw = Math.atan2(-(x - cx), -(z - cz));
      pitch = Math.atan2(y - cy, Math.hypot(x - cx, z - cz));
    }
    out.yaw = yaw;
    out.pitch = pitch;
    return out;
  }

  private followBody(input: CameraInput, dt: number): void {
    const s = this.follow;
    const far = Math.abs(input.x - (s[0] as number)) > CAMERA.snap || Math.abs(input.y - (s[3] as number)) > CAMERA.snap || Math.abs(input.z - (s[6] as number)) > CAMERA.snap;
    if (!this.started || far) {
      s.fill(0);
      s[0] = s[2] = input.x;
      s[3] = s[5] = input.y;
      s[6] = s[8] = input.z;
      this.started = false;
      return;
    }
    springTo(s, 0, input.x, CAMERA.followH, dt);
    springTo(s, 3, input.y, CAMERA.followV, dt);
    springTo(s, 6, input.z, CAMERA.followH, dt);
    // Atraso máximo: a cópia nunca fica longe do corpo (queda livre, Rasteira, Pé de Vento).
    const lx = (s[0] as number) - input.x;
    const lz = (s[6] as number) - input.z;
    const lag = Math.sqrt(lx * lx + lz * lz);
    if (lag > CAMERA.maxLagH) {
      s[0] = input.x + (lx / lag) * CAMERA.maxLagH;
      s[6] = input.z + (lz / lag) * CAMERA.maxLagH;
    }
    const ly = (s[3] as number) - input.y;
    if (Math.abs(ly) > CAMERA.maxLagV) s[3] = input.y + Math.sign(ly) * CAMERA.maxLagV;
  }

  private blendFraming(input: CameraInput, world: CameraWorld, dt: number): void {
    const w = this.weights;
    const rates = CAMERA.rates;
    // No primeiro quadro os pesos encaixam.
    const k = this.started ? dt : Infinity;
    const running = input.grounded && !input.swimming && !input.crouching ? Math.max(0, Math.min(1, (input.speed - MOVE.walkSpeed) / (MOVE.sprintSpeed - MOVE.walkSpeed))) : 0;
    w.sprint = approach(w.sprint, running, rates.sprint, k);
    w.crouch = approach(w.crouch, input.crouching && !input.swimming ? 1 : 0, rates.crouch, k);
    w.swim = approach(w.swim, input.swimming ? 1 : 0, rates.swim, k);
    w.carried = approach(w.carried, input.carried ? 1 : 0, rates.carried, k);
    const s = this.follow;
    const covered = coveredAt(world, s[0] as number, (s[3] as number) + CAMERA.normal.pivot - 0.2, s[6] as number, CAMERA.coverHeight);
    w.covered = approach(w.covered, covered ? 1 : 0, rates.covered, k);
    w.aim = approach(w.aim, input.aiming ? 1 : 0, input.aiming ? rates.aimIn : rates.aimOut, k);
    const f = this.framing;
    // Campo a campo (sem acesso por chave: o caminho genérico aloca).
    f.arm = mix(w, "arm");
    f.shoulder = mix(w, "shoulder");
    f.pivot = mix(w, "pivot");
    f.fov = mix(w, "fov");
    f.arm *= 1 + w.covered * (CAMERA.coveredArm - 1);
    f.shoulder += w.covered * (CAMERA.coveredShoulder - f.shoulder);
    const a = CAMERA.aim;
    f.arm += w.aim * (a.arm - f.arm);
    f.shoulder += w.aim * (a.shoulder - f.shoulder);
    f.pivot += w.aim * (a.pivot - f.pivot);
    f.fov += w.aim * (a.fov - f.fov);
  }
}

export const eyeFor = (crouching: boolean): number => (crouching ? MOVE.crouchEyeHeight : MOVE.eyeHeight);
